import { useState, useEffect, useRef } from 'react';

// 1. useButtonPress — returns press handlers + scale style
export const useButtonPress = () => {
  const [isPressed, setIsPressed] = useState(false);
  return {
    onTouchStart: () => setIsPressed(true),
    onTouchEnd:   () => setIsPressed(false),
    onMouseDown:  () => setIsPressed(true),
    onMouseUp:    () => setIsPressed(false),
    onMouseLeave: () => setIsPressed(false),
    style: {
      transform:  isPressed ? 'scale(0.95)' : 'scale(1)',
      transition: 'transform 0.1s ease-out',
    },
  };
};

// 2. XPFloat — floating +XP label that auto-removes after 1.2s
export const XPFloat = ({ amount, onComplete }) => {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => { setVisible(false); onComplete?.(); }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!visible) return null;
  return (
    <div style={{
      position: 'absolute',
      animation: 'xpFloat 1.2s ease-out forwards',
      color: '#FFD740',
      fontWeight: 700,
      fontSize: '14px',
      pointerEvents: 'none',
    }}>
      +{amount} XP ⭐
    </div>
  );
};

// Internal particle used by ConfettiBurst
const ConfettiParticle = ({ x, delay, color, size, endX, endY }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    setTimeout(() => {
      if (ref.current) {
        ref.current.style.transform = `translate(${endX}px, ${endY}px) scale(0.3)`;
        ref.current.style.opacity = '0';
      }
    }, delay * 1000 + 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div ref={ref} style={{
      position: 'absolute',
      left: `${x}%`,
      top: '50%',
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: color,
      opacity: 1,
      transition: `all 1s ease-out ${delay}s`,
    }} />
  );
};

// 3. ConfettiBurst — 20-particle burst triggered by boolean prop
export const ConfettiBurst = ({ trigger, colors }) => {
  const [particles, setParticles] = useState([]);
  useEffect(() => {
    if (!trigger) return;
    const cols = colors || ['#448AFF', '#FFD740', '#00D4AA', '#4ADE80'];
    const items = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x:     Math.random() * 100,
      delay: Math.random() * 0.3,
      color: cols[Math.floor(Math.random() * cols.length)],
      size:  4 + Math.random() * 4,
      endX:  Math.cos(Math.random() * Math.PI * 2) * (40 + Math.random() * 60),
      endY:  Math.sin(Math.random() * Math.PI * 2) * (40 + Math.random() * 60) - 40,
    }));
    setParticles(items);
    const t = setTimeout(() => setParticles([]), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {particles.map(p => <ConfettiParticle key={p.id} {...p} />)}
    </div>
  );
};

// 4. StreakCounter — rolls up/down when value changes
export const StreakCounter = ({ value }) => {
  const [display, setDisplay] = useState(value);
  const [rolling, setRolling] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (value !== prev.current) {
      setRolling(true);
      setTimeout(() => { setDisplay(value); setRolling(false); }, 300);
      prev.current = value;
    }
  }, [value]);
  return (
    <span style={{
      display:    'inline-block',
      transition: 'transform 0.3s ease-out',
      transform:  rolling ? 'translateY(-8px)' : 'translateY(0)',
      opacity:    rolling ? 0.5 : 1,
    }}>
      🔥 {display}
    </span>
  );
};

// 5. TappableCard — scales down on press/touch
export const TappableCard = ({ children, onClick, className, style }) => {
  const [tapped, setTapped] = useState(false);
  return (
    <div
      className={className}
      onClick={onClick}
      onTouchStart={() => setTapped(true)}
      onTouchEnd={()   => setTimeout(() => setTapped(false), 150)}
      onMouseDown={() => setTapped(true)}
      onMouseUp={()    => setTimeout(() => setTapped(false), 150)}
      style={{
        ...style,
        transform:  tapped ? 'scale(0.97)' : 'scale(1)',
        transition: 'transform 0.1s ease-out',
        cursor:     'pointer',
      }}
    >
      {children}
    </div>
  );
};

// 6. ShakeAnimation — shakes children when trigger flips truthy
export const ShakeAnimation = ({ trigger, children }) => {
  const [shaking, setShaking] = useState(false);
  useEffect(() => {
    if (trigger) {
      setShaking(true);
      setTimeout(() => setShaking(false), 400);
    }
  }, [trigger]);
  return (
    <div style={{ animation: shaking ? 'shake 0.4s ease-out' : 'none' }}>
      {children}
    </div>
  );
};

// 7. SlideTransition — slides children in from left or right
export const SlideTransition = ({ children, direction = 'right' }) => (
  <div style={{
    animation: direction === 'right'
      ? 'slideInRight 0.3s ease-out'
      : 'slideInLeft 0.3s ease-out',
  }}>
    {children}
  </div>
);
